import React, { useMemo } from 'react';
import get from 'lodash/get';
import capitalize from 'lodash/capitalize';
import dayjs from 'dayjs';
import locale from 'dayjs/locale/pl';
import weekdayPlugin from 'dayjs/plugin/weekday';
import objectPlugin from 'dayjs/plugin/toObject';
import isTodayPlugin from 'dayjs/plugin/isToday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { FormatWithPrecision } from 'utility/numberFormatting';

import * as Styled from './Calendar.styles';
import { Tooltip } from '@mui/material';

dayjs.extend(weekdayPlugin);
dayjs.extend(objectPlugin);
dayjs.extend(isTodayPlugin);
dayjs.extend(weekOfYear);

export const Calendar = ({ events }) => {
  const [popoverData, setPopoverData] = React.useState(null);

  const calendarWeeks = useMemo(() => {
    const now = dayjs().locale({
      ...locale,
    });
    const nextMonth = now.add(1, 'month').month();

    const monthWeeks = {};

    let iterationDate = now.startOf('month').weekday(0);

    while (iterationDate.weekday(0).toObject().months !== nextMonth) {
      monthWeeks[`${iterationDate.weekday(0).year()}-${iterationDate.week()}`] = [
        ...(monthWeeks[`${iterationDate.weekday(0).year()}-${iterationDate.week()}`] || []),
        iterationDate,
      ];

      iterationDate = iterationDate.add(1, 'day');
    }

    return Object.values(monthWeeks).map((week) =>
      week.map((day) => ({
        date: day.format('DD.MM.YYYY'),
        day: day.date(),
        isToday: day.isToday(),
        isCurrentMonth: day.month() === now.month(),
        events: get(
          events.find((event) => dayjs(event.eventDateTime).format('YYYY-MM-DD') === day.format('YYYY-MM-DD')),
          'documentEvents',
          [],
        ),
      })),
    );
  }, [events]);

  return (
    <Styled.Wrapper>
      <Styled.Title>
        <span>Kalendarz faktur</span>
        <span className="date">
          {capitalize(
            dayjs()
              .locale({
                ...locale,
              })
              .format('MMMM, YYYY'),
          )}
        </span>
      </Styled.Title>

      <Styled.Calendar>
        <Styled.CalendarRow>
          <Styled.Day className="weekName">Pn</Styled.Day>
          <Styled.Day className="weekName">Wt</Styled.Day>
          <Styled.Day className="weekName">Śr</Styled.Day>
          <Styled.Day className="weekName">Cz</Styled.Day>
          <Styled.Day className="weekName">Pt</Styled.Day>
          <Styled.Day className="weekName">So</Styled.Day>
          <Styled.Day className="weekName">Nd</Styled.Day>
        </Styled.CalendarRow>

        {calendarWeeks.map((week, index) => (
          <Styled.CalendarRow key={index}>
            {week.map((day) => (
              <Tooltip
                key={day.date}
                title={day && day.events && day.events.length > 0 ? 'Zaplanowane faktury: ' + day.events.length : ''}
                placement="top"
                arrow
                enterDelay={300}
                enterNextDelay={300}
              >
                <Styled.Day
                  onClick={(event) =>
                    day.events.length > 0
                      ? setPopoverData({
                          anchorEl: event.currentTarget,
                          events: day.events,
                          date: day.date,
                        })
                      : null
                  }
                  className={classnames({
                    currentMonth: day.isCurrentMonth,
                    today: day.isToday,
                    hasEvents: day.events.length > 0,
                  })}
                >
                  {day.day}
                </Styled.Day>
              </Tooltip>
            ))}
          </Styled.CalendarRow>
        ))}
      </Styled.Calendar>

      {!!popoverData && (
        <Styled.Popover
          open
          onClose={() => setPopoverData(null)}
          anchorEl={popoverData.anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Styled.DropdownContent>
            <Styled.PopoverTitle>Zaplanowane faktury</Styled.PopoverTitle>
            <Styled.PopoverDate>{popoverData.date}</Styled.PopoverDate>
            <Styled.PopoverDivider />
            {popoverData.events.map((event, index) => (
              <Styled.PopoverEvent key={index}>
                <div className="name">{event.buyerName}</div>
                <div className="price">
                  {FormatWithPrecision(event.amountToPay)} {event.currencyCode}
                </div>
              </Styled.PopoverEvent>
            ))}
          </Styled.DropdownContent>
        </Styled.Popover>
      )}
    </Styled.Wrapper>
  );
};

Calendar.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      eventDateTime: PropTypes.string.isRequired,
      documentEvents: PropTypes.arrayOf(
        PropTypes.shape({
          fullName: PropTypes.string,
          buyerName: PropTypes.string,
          amountToPay: PropTypes.number,
          currencyCode: PropTypes.string,
        }),
      ).isRequired,
    }),
  ),
};
